import { useMemo, useState } from 'react';
import { uniqBy } from 'lodash';
import { QuestionType_Enum_ } from 'models';
import { BaseTableMetric } from './types';
import { useCurrentCompany } from 'utils/hooks';
import { useFeatureFlags } from 'containers/Navigation';
import { FrameworkRefs } from 'containers/Esrs/pieces/Assessment';

export const useTableFilters = <T extends BaseTableMetric<any>>(data: T[]) => {
  const [searchValue, setSearchValue] = useState('');
  const [selectedFramework, setSelectedFramework] = useState('All');
  const [selectedType, setSelectedType] = useState('All');
  const [selectedSubcategory, setSelectedCategory] = useState('All');
  const { company } = useCurrentCompany();

  const {
    esrs: hasESRSAccess,
    vsme: hasVSMEAccess,
    ifrs: hasIFRSAccess,
    tcfd: hasTCFDAccess,
    esrsOilAndGas: hasESRSOilAndGasAccess,
    entitySpecific: hasEntitySpecificAccess,
    sasb: hasSASBAccess,
  } = useFeatureFlags({ company });

  const frameworkOptions = useMemo(() => {
    return [
      { label: 'All', value: 'All' },
      ...uniqBy(
        data.map((item) => ({
          label: item.framework.toUpperCase(),
          value: item.framework,
        })),
        'label'
      ),
    ];
  }, [data]);

  const typeOptions = useMemo(() => {
    return [
      { label: 'All', value: 'All' },
      ...uniqBy(
        data.map((item) => ({
          label: item.metric.metricType !== QuestionType_Enum_.Decimal_ ? 'Narrative' : 'Numeric',
          value: item.metric.metricType !== QuestionType_Enum_.Decimal_ ? 'Narrative' : 'Numeric',
        })),
        'label'
      ),
    ];
  }, [data]);

  const subcategoryOptions = useMemo(() => {
    return [
      { label: 'All', value: 'All' },
      ...uniqBy(
        data.map((item) => ({
          label: item.subCategory,
          value: item.subCategory,
        })),
        'label'
      ),
    ];
  }, [data]);

  const filteredData = useMemo(() => {
    return data.filter(
      (item) =>
        (hasVSMEAccess || item.framework !== FrameworkRefs.vsme) &&
        (hasESRSAccess || item.framework !== FrameworkRefs.esrs) &&
        (hasIFRSAccess || item.framework !== FrameworkRefs.ifrs) &&
        (hasTCFDAccess || item.framework !== FrameworkRefs.tcfd) &&
        (hasESRSOilAndGasAccess || item.framework !== FrameworkRefs['esrs-oil-and-gas']) &&
        (hasEntitySpecificAccess || item.framework !== FrameworkRefs['entity-specific']) &&
        (hasSASBAccess || item.framework !== FrameworkRefs.sasb) &&
        (selectedFramework !== 'All' ? item.framework === selectedFramework : true) &&
        (selectedType !== 'All'
          ? selectedType === 'Numeric'
            ? item.type === QuestionType_Enum_.Decimal_
            : item.type !== QuestionType_Enum_.Decimal_
          : true) &&
        (selectedSubcategory !== 'All' ? item.subCategory === selectedSubcategory : true) &&
        (searchValue !== '' ? item.title.toLowerCase().includes(searchValue.toLowerCase()) : true)
    );
  }, [data, selectedFramework, selectedType, selectedSubcategory, searchValue]);

  return {
    searchValue,
    setSearchValue,
    selectedFramework,
    setSelectedFramework,
    selectedType,
    setSelectedType,
    selectedSubcategory,
    setSelectedCategory,
    frameworkOptions,
    typeOptions,
    subcategoryOptions,
    filteredData,
  };
};
